import Button from "@mui/material/Button";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSnackbar } from "notistack";

const Camera = ({
  active,
  onSave,
}: {
  active: boolean;
  onSave: (b: Blob) => void;
}) => {
  const [streaming, setStreaming] = useState(false);
  const [imgSrc, setImgSrc] = useState("");
  const video = useRef<HTMLVideoElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    (async () => {
      if (!active || streaming || !video.current || !canvas.current) return;
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: { width: 9999, facingMode: "environment" }, // take a picture of a doc with the best camera available
        });
        video.current.srcObject = stream;
        video.current.play();
        setStreaming(true);
      } catch (e) {
        console.error(e);
        enqueueSnackbar("Problem obtaining MediaStream.", {
          variant: "error",
        });
      }
    })();
  }, [active, streaming, video, canvas, enqueueSnackbar]);

  useEffect(() => {
    if (!active && streaming && video.current) {
      const stream = (video.current.srcObject as any).getTracks()[0];
      video.current.pause();
      video.current.src = "";
      stream.stop();
      setStreaming(false);
    }
  }, [active, streaming, video]);

  const takePhoto = useCallback(() => {
    if (!canvas.current || !video.current) return;

    const context = canvas.current.getContext("2d");
    if (!context) return;

    const settings = (video.current.srcObject as any)
      .getTracks()[0]
      .getSettings();

    canvas.current.width = settings.width;
    canvas.current.height = settings.height;
    context.drawImage(video.current, 0, 0, settings.width, settings.height);

    const dataUrl = canvas.current.toDataURL("image/jpeg", 0.95);
    setImgSrc(dataUrl);
  }, [canvas, video]);

  const onSaveClick = useCallback(() => {
    if (!canvas.current) return;
    canvas.current.toBlob(
      (b) => {
        if (b) onSave(b);
      },
      "image/jpeg",
      0.95
    );
  }, [canvas, onSave]);

  return (
    <div className="flex flex-col md:flex-row gap-4 text-center">
      <div className="flex-1 flex flex-col gap-4">
        <h2 className="font-head">TAKE PHOTO</h2>
        <video ref={video} className="w-full">
          Video stream not available.
        </video>
        <div>
          <Button onClick={takePhoto} variant="outlined">
            Take photo
          </Button>
        </div>
        <canvas ref={canvas} style={{ display: "none" }}></canvas>
      </div>
      <div className="flex-1 flex flex-col gap-4">
        <h2 className="font-head">PHOTO PREVIEW</h2>
        {imgSrc ? (
          <img src={imgSrc} alt="preview of photo" className="w-full" />
        ) : null}
        <div>
          <Button onClick={onSaveClick} variant="outlined">
            Save photo
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Camera;
